import * as React from "react"
import { useDropzone, type FileRejection } from "react-dropzone"
import { ImageIcon, Pencil, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { PROFILE_AVATAR_MAX_BYTES, PROFILE_BANNER_MAX_BYTES } from "@/lib/profile-avatar-media"
import { EntityLogo } from "@/components/ui/entity-logo"
import { InitialsAvatar } from "@/components/ui/initials-avatar"
import { Button } from "@/components/ui/button"
import { TenantLogoWatermark } from "@/components/ui/tenant-logo-watermark"

const DEFAULT_ACCEPT: Record<string, string[]> = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/gif": [".gif"],
  "image/webp": [".webp"],
}

const DEFAULT_MAX = PROFILE_AVATAR_MAX_BYTES

function dropzoneRejectMessage(
  maxSizeBytes: number,
  rejectedFiles: FileRejection[],
  fileTypeErrorMessage?: string
): string {
  const first = rejectedFiles[0]
  if (!first) return "File was not accepted."
  const codes = first.errors.map((e) => e.code)
  if (codes.includes("file-too-large")) {
    const mb = Math.round(maxSizeBytes / (1024 * 1024))
    return `Image is too large. Maximum size is ${mb} MB.`
  }
  if (codes.includes("file-invalid-type")) {
    return fileTypeErrorMessage ?? "File type not accepted. Use PNG, JPEG, GIF, or WebP."
  }
  return first.errors.map((e) => e.message).filter(Boolean).join(" ") || "Could not use that file."
}

export interface ImageUploadFieldProps {
  /** Round avatar (person), wide cover (profile banner), or organisation logo (120×120, squared). */
  variant: "avatar" | "logo" | "banner"
  /** Stabilises identicon when no photo (e.g. profile id, email). */
  avatarColorSeed?: string
  /** Persisted image URL from API; shown when there is no local preview blob. */
  serverImageUrl?: string | null
  /** Persist selected file — typically `/api/accounts/me/avatar` via injected callbacks. */
  uploadAvatar?: (file: File) => Promise<unknown>
  /** Remove persisted avatar on the server. */
  deleteAvatar?: () => Promise<unknown>
  isUploadingAvatar?: boolean
  isDeletingAvatar?: boolean
  /** Persist to `/api/profiles/my/banner` when variant is `banner`. */
  uploadBanner?: (file: File) => Promise<unknown>
  deleteBanner?: () => Promise<unknown>
  isUploadingBanner?: boolean
  isDeletingBanner?: boolean
  /** When variant is `logo`: POST file to `/api/organisations/managed/{slug}/logo/`. */
  uploadLogo?: (file: File) => Promise<unknown>
  deleteLogo?: () => Promise<unknown>
  isUploadingLogo?: boolean
  isDeletingLogo?: boolean
  className?: string
  disabled?: boolean
  accept?: Record<string, string[]>
  maxSize?: number
  /** Optional async validation (e.g. square logo dimensions) before upload. */
  validateFile?: (file: File) => Promise<string | null>
  fileTypeErrorMessage?: string
  /** Shown under the control; set `null` when upload is wired for avatars. */
  persistenceNote?: string | null
}

/**
 * Avatar, cover banner, or logo drop target: local preview, optional server URL, and optional multipart upload to the API.
 */
export function ImageUploadField({
  variant,
  avatarColorSeed = "profile",
  serverImageUrl,
  uploadAvatar,
  deleteAvatar,
  isUploadingAvatar = false,
  isDeletingAvatar = false,
  uploadBanner,
  deleteBanner,
  isUploadingBanner = false,
  isDeletingBanner = false,
  uploadLogo,
  deleteLogo,
  isUploadingLogo = false,
  isDeletingLogo = false,
  className,
  disabled = false,
  accept = DEFAULT_ACCEPT,
  maxSize,
  validateFile,
  fileTypeErrorMessage,
  persistenceNote = "Preview only — upload to the server is not configured for this control.",
}: ImageUploadFieldProps) {
  const effectiveMaxSize = maxSize ?? (variant === "banner" ? PROFILE_BANNER_MAX_BYTES : DEFAULT_MAX)

  const [file, setFile] = React.useState<File | null>(null)
  const [preview, setPreview] = React.useState<string | null>(null)
  const [localError, setLocalError] = React.useState<string | null>(null)
  const prevServerImageUrlRef = React.useRef<string | null | undefined>(undefined)

  const displayUrl = preview ?? serverImageUrl ?? null

  const uploading =
    variant === "banner"
      ? isUploadingBanner
      : variant === "logo"
        ? isUploadingLogo
        : isUploadingAvatar
  const deleting =
    variant === "banner"
      ? isDeletingBanner
      : variant === "logo"
        ? isDeletingLogo
        : isDeletingAvatar
  const busy = uploading || deleting

  React.useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const applyLocalFile = React.useCallback((next: File) => {
    setFile(next)
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return URL.createObjectURL(next)
    })
  }, [])

  const clearLocalOnly = React.useCallback(() => {
    setFile(null)
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev)
      return null
    })
  }, [])

  /** When the API/cache updates the stored image URL, drop local preview so `displayUrl` tracks the server (fixes logo/avatar/banner panels that clear preview before props catch up). */
  React.useEffect(() => {
    const next = serverImageUrl ?? null
    const prev = prevServerImageUrlRef.current
    prevServerImageUrlRef.current = next
    if (prev === undefined) return
    if (prev === next) return
    clearLocalOnly()
  }, [serverImageUrl, clearLocalOnly])

  const onDrop = React.useCallback(
    async (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        setLocalError(dropzoneRejectMessage(effectiveMaxSize, rejectedFiles, fileTypeErrorMessage))
        return
      }
      const next = acceptedFiles[0]
      if (!next) return
      setLocalError(null)

      if (validateFile) {
        const validationError = await validateFile(next)
        if (validationError) {
          setLocalError(validationError)
          return
        }
      }

      applyLocalFile(next)

      if (variant === "avatar" && uploadAvatar) {
        try {
          await uploadAvatar(next)
          clearLocalOnly()
        } catch (e) {
          setLocalError(e instanceof Error ? e.message : "Upload failed")
          clearLocalOnly()
        }
      } else if (variant === "banner" && uploadBanner) {
        try {
          await uploadBanner(next)
          clearLocalOnly()
        } catch (e) {
          setLocalError(e instanceof Error ? e.message : "Upload failed")
          clearLocalOnly()
        }
      } else if (variant === "logo" && uploadLogo) {
        try {
          await uploadLogo(next)
          clearLocalOnly()
        } catch (e) {
          setLocalError(e instanceof Error ? e.message : "Upload failed")
          clearLocalOnly()
        }
      }
    },
    [applyLocalFile, clearLocalOnly, effectiveMaxSize, fileTypeErrorMessage, uploadAvatar, uploadBanner, uploadLogo, validateFile, variant]
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept,
    maxSize: effectiveMaxSize,
    maxFiles: 1,
    multiple: false,
    disabled: disabled || uploading,
    noClick: false,
    noKeyboard: false,
  })

  const handleRemove = async () => {
    setLocalError(null)
    if (file) {
      clearLocalOnly()
      return
    }
    if (variant === "banner" && serverImageUrl && deleteBanner) {
      try {
        await deleteBanner()
      } catch (e) {
        setLocalError(e instanceof Error ? e.message : "Could not remove cover image")
      }
      return
    }
    if (variant === "logo" && serverImageUrl && deleteLogo) {
      try {
        await deleteLogo()
      } catch (e) {
        setLocalError(e instanceof Error ? e.message : "Could not remove logo")
      }
      return
    }
    if (serverImageUrl && deleteAvatar) {
      try {
        await deleteAvatar()
      } catch (e) {
        setLocalError(e instanceof Error ? e.message : "Could not remove photo")
      }
    }
  }

  const showPencilOverlay =
    (variant === "avatar" || variant === "banner" || variant === "logo") &&
    !disabled &&
    !uploading &&
    !deleting

  const showRemove =
    !!file ||
    (variant === "banner"
      ? !!serverImageUrl && !!deleteBanner
      : variant === "logo"
        ? !!serverImageUrl && !!deleteLogo
        : !!serverImageUrl && !!deleteAvatar)

  const shape =
    variant === "avatar"
      ? "rounded-full"
      : "rounded-xl"
  const rootClass = cn(
    "group relative outline-none",
    variant === "avatar"
      ? "size-[120px] shrink-0"
      : variant === "banner"
        ? "h-60 w-full shrink-0"
        : "size-[120px] shrink-0",
    disabled || busy ? "cursor-not-allowed opacity-60" : "cursor-pointer",
    shape,
    variant === "avatar"
      ? "border-0 bg-transparent"
      : variant === "banner"
        ? "overflow-hidden border border-border/60 bg-muted/40"
        : "overflow-hidden border-0 bg-transparent",
    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    isDragActive && "ring-2 ring-primary/40",
    disabled && "cursor-not-allowed opacity-50"
  )

  return (
    <div className={cn("space-y-3", className)}>
      <div
        className={cn(
          "flex gap-3",
          variant === "banner"
            ? "flex-col"
            : "flex-col sm:flex-row sm:items-center sm:gap-4"
        )}
      >
        <div
          {...getRootProps({
            className: rootClass,
            "aria-label":
              variant === "avatar"
                ? "Profile photo — drop an image or click to choose"
                : variant === "banner"
                  ? "Cover image — drop an image or click to choose"
                  : "Organisation logo — drop an image or click to choose",
          })}
        >
          <input {...getInputProps()} />

          {variant === "avatar" ? (
            <div className="relative z-0 size-full">
              <InitialsAvatar
                src={displayUrl}
                colorSeed={avatarColorSeed}
                framed
                className="size-full"
                alt=""
              />
              {isUploadingAvatar && (
                <div className="absolute inset-0 z-20 flex items-center justify-center rounded-full bg-background/70">
                  <Loader2 className="size-6 animate-spin text-foreground" aria-hidden />
                </div>
              )}
            </div>
          ) : variant === "banner" ? (
            <div className="relative z-0 flex h-full w-full items-center justify-center overflow-hidden">
              {displayUrl ? (
                <img src={displayUrl} alt="" className="h-full w-full object-cover" />
              ) : (
                <div className="relative isolate h-full w-full">
                  <TenantLogoWatermark />
                  <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center">
                    <ImageIcon className="size-10 text-muted-foreground opacity-60" aria-hidden />
                  </div>
                </div>
              )}
              {isUploadingBanner && (
                <div className="absolute inset-0 z-20 flex items-center justify-center bg-background/70">
                  <Loader2 className="size-8 animate-spin text-foreground" aria-hidden />
                </div>
              )}
            </div>
          ) : (
            <div className="relative z-0 size-full">
              <EntityLogo src={displayUrl} size="editor" framed className="size-full" />
              {isUploadingLogo && (
                <div className={cn("absolute inset-0 z-20 flex items-center justify-center rounded-xl bg-background/70")}>
                  <Loader2 className="size-6 animate-spin text-foreground" aria-hidden />
                </div>
              )}
            </div>
          )}

          {showPencilOverlay && (
            <div
              className={cn(
                "pointer-events-none absolute inset-0 z-10 flex items-center justify-center",
                shape,
                "bg-black/0 transition-[background-color] group-hover:bg-black/40",
                isDragActive && "bg-black/40"
              )}
            >
              <Pencil
                aria-hidden
                className={cn(
                  "size-5 text-white drop-shadow-sm opacity-0 transition-opacity",
                  variant === "banner" && "size-6",
                  "group-hover:opacity-100",
                  isDragActive && "opacity-100"
                )}
              />
            </div>
          )}
        </div>

        {showRemove && !disabled ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={(e) => {
              e.stopPropagation()
              void handleRemove()
            }}
            disabled={busy}
            className={cn(
              "self-start",
              variant !== "banner" && "self-center sm:self-end"
            )}
          >
            {deleting ? "Removing…" : variant === "banner" ? "Remove cover" : "Remove image"}
          </Button>
        ) : null}
      </div>

      {(localError || persistenceNote) && (
        <p className={cn("text-xs", localError ? "text-destructive" : "text-muted-foreground")}>
          {localError ?? persistenceNote}
        </p>
      )}
    </div>
  )
}
